/**
 * FinaleGauntlet — pure logic for the Stage 9 multi-phase finale gauntlet.
 *
 * Four phases replace the previous single Halloway+Astor boss fight:
 *
 *   Phase 0 — Cordon      : escalating security waves (handled by stageConfig
 *                            sections). A checkpoint fires after all cordon waves
 *                            clear and before the boss rush begins.
 *   Phase 1 — Old Faces   : returning-boss rush — Doorman Rex → The Conductor →
 *                            Captain Vlad → Lord Astor-Pemberton, fought
 *                            back-to-back with existing movesets/AI.
 *                            Dying during the rush respawns at the checkpoint
 *                            saved before it began (first boss slot).
 *   Phase 2 — The Praetor : handoff to the new final boss (ticket MY-1977).
 *                            A checkpoint fires before The Praetor spawns.
 *   Phase 3 — PM Halloway : weak one-hit-KO coda after the Praetor.
 *
 * No Phaser import — fully testable in Node.
 */

// ── Phase types ───────────────────────────────────────────────────────────────

/** Phase 0: escalating waves; data lives in stageConfig sections. */
export interface CordonPhase {
  kind: 'cordon';
}

/** Phase 1: bosses fought back-to-back in the listed order. */
export interface BossRushPhase {
  kind: 'boss-rush';
  bossIds: readonly string[];
}

/**
 * Phase 2: checkpoint + handoff to the Praetor final boss.
 * The Phaser adapter spawns `nextBossId` after saving the checkpoint.
 */
export interface PraetorHandoffPhase {
  kind: 'praetor-handoff';
  /** Id of the final boss to spawn once the checkpoint is saved. */
  nextBossId: string;
}

/**
 * Phase 3: PM Halloway exposed — weak one-hit KO humiliation beat (MY-1978).
 * Spawns PM Halloway alone with no guards. Any hit KOs him.
 * On KO, the gauntlet completes and the existing true-ending route continues.
 */
export interface PMHallowayPhase {
  kind: 'pm-halloway';
  /** Id of the weak entity to spawn (pm-halloway). */
  nextBossId: string;
}

export type GauntletPhase = CordonPhase | BossRushPhase | PraetorHandoffPhase | PMHallowayPhase;

/** Full gauntlet configuration — ordered list of phases. */
export interface FinaleGauntletConfig {
  readonly phases: readonly GauntletPhase[];
}

// ── Stage 9 gauntlet ──────────────────────────────────────────────────────────

/**
 * The S9 finale gauntlet (MY-1976).
 *
 * Phase 0 (cordon) defers to the three STAGE_9 sections already defined in
 * stageConfig — no duplication of wave data here.
 *
 * Phase 1 (boss rush) replays prior bosses in escalating order. Lord
 * Astor-Pemberton (halloway-astor) is demoted from co-final to the last slot
 * of the rush; existing AI + movesets are reused unchanged.
 *
 * Phase 2 (praetor handoff) seeds the new final encounter. Full Phaser
 * wiring deferred to MY-1977 (THE PRAETOR ticket).
 *
 * Phase 3 (PM Halloway) is the MY-1978 coda: a weak, non-boss entity after
 * the Praetor falls.
 */
export const STAGE_9_GAUNTLET: FinaleGauntletConfig = {
  phases: [
    { kind: 'cordon' },
    {
      kind: 'boss-rush',
      bossIds: ['doorman-rex', 'the-conductor', 'captain-vlad', 'halloway-astor'],
    },
    {
      kind: 'praetor-handoff',
      // TODO(MY-1977): wire the-praetor Phaser entity + BossBrain once that ticket ships.
      nextBossId: 'the-praetor',
    },
    {
      kind: 'pm-halloway',
      nextBossId: 'pm-halloway',
    },
  ],
};

// ── State ─────────────────────────────────────────────────────────────────────

export interface GauntletState {
  /** 0-indexed phase in config.phases (0=cordon, 1=boss-rush, 2=praetor, 3=PM Halloway). */
  phaseIndex: number;
  /** Within a boss-rush phase: which boss slot is active (0-indexed). */
  bossRushIndex: number;
  /**
   * Phase index saved at the last checkpoint.
   * On death + continue, the gauntlet rewinds to this index.
   */
  checkpointPhaseIndex: number;
  /**
   * Boss-rush slot saved at the last checkpoint.
   * Dying mid-rush respawns at this slot (normally 0 — start of the rush).
   */
  checkpointBossRushIndex: number;
}

/** Fresh gauntlet state — player starts at the cordon phase. */
export function initialGauntletState(): GauntletState {
  return {
    phaseIndex: 0,
    bossRushIndex: 0,
    checkpointPhaseIndex: 0,
    checkpointBossRushIndex: 0,
  };
}

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Return the currently active phase, or null when phaseIndex has moved
 * beyond all phases (gauntlet complete).
 */
export function currentGauntletPhase(
  state: GauntletState,
  config: FinaleGauntletConfig,
): GauntletPhase | null {
  return config.phases[state.phaseIndex] ?? null;
}

/**
 * Return the id of the boss the player should fight right now (boss-rush
 * phase only), or null when not in a boss-rush phase or all bosses are done.
 */
export function currentBossRushBossId(
  state: GauntletState,
  config: FinaleGauntletConfig,
): string | null {
  const phase = currentGauntletPhase(state, config);
  if (!phase || phase.kind !== 'boss-rush') return null;
  return phase.bossIds[state.bossRushIndex] ?? null;
}

/** True when all gauntlet phases have been cleared. */
export function isGauntletComplete(state: GauntletState, config: FinaleGauntletConfig): boolean {
  return state.phaseIndex >= config.phases.length;
}

// ── State transitions (pure — return new state) ───────────────────────────────

/**
 * Save a checkpoint at the current gauntlet position.
 *
 * Call this:
 *   - After all cordon waves clear (before spawning boss-rush phase).
 *   - Before spawning The Praetor (praetor-handoff phase).
 */
export function saveGauntletCheckpoint(state: GauntletState): GauntletState {
  return {
    ...state,
    checkpointPhaseIndex: state.phaseIndex,
    checkpointBossRushIndex: state.bossRushIndex,
  };
}

/**
 * Rewind the gauntlet to the last saved checkpoint.
 * Call when the player exhausts lives and continues.
 */
export function respawnAtGauntletCheckpoint(state: GauntletState): GauntletState {
  return {
    ...state,
    phaseIndex: state.checkpointPhaseIndex,
    bossRushIndex: state.checkpointBossRushIndex,
  };
}

/**
 * Advance from the current non-boss-rush phase to the next phase.
 * (Cordon → boss-rush transition; call after all cordon sections clear.)
 */
export function advanceGauntletPhase(state: GauntletState): GauntletState {
  return {
    ...state,
    phaseIndex: state.phaseIndex + 1,
    bossRushIndex: 0,
  };
}

/**
 * Mark the current boss-rush slot as defeated and move to the next boss.
 * When the last boss in the rush falls, automatically advances to the next
 * gauntlet phase (praetor-handoff).
 *
 * Returns state unchanged when not currently in a boss-rush phase.
 */
export function advanceBossRush(state: GauntletState, config: FinaleGauntletConfig): GauntletState {
  const phase = config.phases[state.phaseIndex];
  if (!phase || phase.kind !== 'boss-rush') return state;

  const nextSlot = state.bossRushIndex + 1;
  if (nextSlot >= phase.bossIds.length) {
    return {
      ...state,
      phaseIndex: state.phaseIndex + 1,
      bossRushIndex: 0,
    };
  }
  return { ...state, bossRushIndex: nextSlot };
}
